import { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import Link from 'next/link';
import { Drawer, DrawerProps } from '../drawer';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';
import {
  linkConfigSelector,
  projectIdSelector,
  accessTokenOrganizationIdSelector,
} from '@algoan/dashboard/store/selectors';
import {
  buildUrl,
  isAnonymousCustomer,
  useClipboard,
  dashboardTabUrl,
  getDashboardTabRoute,
} from '@algoan/dashboard/utils';
import { Button, Input, SelectDropdown, DropdownItem } from '../../shared-components/atomic-components';
import throttle from 'lodash/throttle';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { CopyIcon } from '../../shared-components/icons';
import { NotificationVariant } from '@algoan/dashboard/model';
import { notify, searchCustomers } from '@algoan/dashboard/store/thunks';
import { addAlpha } from '@algoan/deep-dive/theme';

interface StringParam {
  description: string;
  key: string;
  required?: boolean;
  type: 'string';
  value: string;
  isCustomIdentifier?: boolean;
}

interface ListParam {
  description: string;
  key: string;
  options: DropdownItem[];
  required?: boolean;
  type: 'list';
  value: string | undefined;
}

type QueryParam = StringParam | ListParam;

export type LinkProps = Pick<DrawerProps, 'open' | 'onClose'>;

export const LinkDrawer: React.FC<LinkProps> = ({ open, onClose }) => {
  const intl = useIntl();
  const linkConfig = useSelector(linkConfigSelector);
  const clipboard = useClipboard();
  const dispatch = useDispatch();
  const [customerWithSameCustomIdentifier, setCustomerWithSameCustomIdentifier] = useState<{
    customerId: string;
    isAnonymous: boolean;
  }>();
  const organizationId = useSelector(accessTokenOrganizationIdSelector) as string;
  const projectId = useSelector(projectIdSelector) as string;

  const convertToOptions = (options: string[] | undefined): DropdownItem[] => {
    if (options && Array.isArray(options) && options.length > 0) {
      return options.map((option) => ({ key: option, label: option }));
    } else return [];
  };

  const getInitialQueryParams = useCallback((): QueryParam[] => {
    const params = linkConfig?.params ?? [];
    return params.map((param) =>
      param.type && param.type === 'list'
        ? {
            type: 'list',
            key: param.name,
            value: '',
            required: param.required,
            description: param.description,
            options: convertToOptions(param.options),
          }
        : {
            type: 'string',
            key: param.name,
            value: '',
            required: param.required,
            description: param.description,
            isCustomIdentifier: param.isCustomIdentifier,
          },
    );
  }, [linkConfig]);

  const [queryParams, setQueryParams] = useState(getInitialQueryParams());

  useEffect(() => {
    setQueryParams(getInitialQueryParams());
  }, [getInitialQueryParams]);

  const handleChange = useCallback(
    (inputIndex: number, newValue: string): void => {
      setQueryParams((prev) => {
        return prev.map((paramValue, index) =>
          index === inputIndex ? { ...paramValue, value: newValue } : paramValue,
        );
      });
    },
    [setQueryParams],
  );

  const handleClose = useCallback((): void => {
    setQueryParams((prev) => prev.map((paramValue) => ({ ...paramValue, value: '' })));
    setCustomerWithSameCustomIdentifier(undefined);
    onClose();
  }, [onClose]);

  const hasRequiredParam = queryParams.some((param) => param.required);
  const hasEmptyRequiredParam = queryParams.some((param) => param.required && param.value === '');
  const url = buildUrl(linkConfig?.url ?? '', queryParams);

  const handleCopy = throttle(async (): Promise<void> => {
    await clipboard.copy(url);
    logAmplitudeEvent(AmplitudeEvent.LINK_COPY);
    dispatch(
      notify({
        type: NotificationVariant.SUCCESS,
        message: {
          id: 'link_drawer.copy_success_notification',
          defaultMessage: 'The link has been copied to the clipboard',
          description: 'Text of the copy success notification',
        },
      }),
    );
  }, 500);

  const checkCustomIdentifier = useCallback(
    async (value: string): Promise<void> => {
      const { resources: customers } = await dispatch(
        searchCustomers([
          {
            key: 'filter',
            value: {
              customIdentifier: value,
            },
          },
        ]),
      );
      const customerData =
        customers.length > 0
          ? { customerId: customers[0].id, isAnonymous: isAnonymousCustomer(customers[0]) }
          : undefined;
      setCustomerWithSameCustomIdentifier(customerData);
    },
    [dispatch],
  );

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={intl.formatMessage({
        id: 'link_drawer.create_link',
        defaultMessage: 'Create a link',
        description: 'The title of the drawer used for link creation',
      })}
    >
      <S.Content data-testid="link-drawer-content">
        <div>
          {queryParams.map((param, index) => (
            <S.ParamContainer key={index}>
              {param.type === 'string' && (
                <S.Input
                  label={param.description}
                  value={param.value}
                  onChange={(e) => handleChange(index, e.target.value)}
                  placeholder={param.description}
                  required={param.required}
                  onBlur={() => param.isCustomIdentifier && checkCustomIdentifier(param.value)}
                  message={
                    param.isCustomIdentifier && customerWithSameCustomIdentifier
                      ? {
                          type: 'warning',
                          content: (
                            <div>
                              {customerWithSameCustomIdentifier.isAnonymous ? (
                                <FormattedMessage
                                  id="link_drawer.existing_anonymous_custom_identifier_warning"
                                  defaultMessage="This application reference already exists and the application has been archived."
                                  description="Warning message when an anonymous application exists with the same custom identifier"
                                />
                              ) : (
                                <>
                                  <FormattedMessage
                                    id="link_drawer.existing_custom_identifier_warning"
                                    defaultMessage="This application reference already exists."
                                    description="Warning message when an application exists with the same custom identifier"
                                  />
                                  <Link
                                    href={dashboardTabUrl}
                                    as={getDashboardTabRoute(
                                      organizationId,
                                      projectId,
                                      customerWithSameCustomIdentifier.customerId,
                                    )}
                                    passHref
                                    legacyBehavior
                                  >
                                    <S.Anchor target="_blank" rel="noreferrer">
                                      <FormattedMessage
                                        id="link_drawer.go_to_application"
                                        defaultMessage="Go to the application."
                                        description="Message to go to the application."
                                      />
                                    </S.Anchor>
                                  </Link>
                                </>
                              )}
                            </div>
                          ),
                        }
                      : undefined
                  }
                />
              )}
              {param.type === 'list' && (
                <>
                  <S.ParamTitle required={param.required}>{param.description}</S.ParamTitle>
                  <SelectDropdown
                    onClick={(selectedOption: DropdownItem) => {
                      handleChange(index, selectedOption.key);
                    }}
                    options={param.options}
                    placeholder={param.description}
                    value={param.value ? { key: param.value, label: param.value } : undefined}
                  />
                </>
              )}
            </S.ParamContainer>
          ))}
        </div>
        <S.LinkSection>
          <S.LinkHeader>
            <S.LinkTitle>
              <FormattedMessage
                id="link_drawer.link_section"
                defaultMessage="Link"
                description="Title of the link section"
              />
            </S.LinkTitle>
            <Button
              variant="secondary"
              icon={CopyIcon}
              onClick={handleCopy}
              disabled={hasEmptyRequiredParam}
              data-testid="link-drawer-copy-link"
            >
              <FormattedMessage
                id="link_drawer.copy_link"
                defaultMessage="Copy"
                description="Label of the copy link button"
              />
            </Button>
          </S.LinkHeader>
          <S.Link disabled={hasEmptyRequiredParam}>{url}</S.Link>
        </S.LinkSection>
        {hasRequiredParam && (
          <S.Footer>
            *
            <FormattedMessage
              id="link_drawer.mandatory"
              defaultMessage="Mandatory"
              description="Legend of mandatory fields"
            />
          </S.Footer>
        )}
      </S.Content>
    </Drawer>
  );
};

const Title = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 19px;
  color: ${({ theme }) => theme.algoanBlue};
`;

const S = {
  Content: styled.div`
    width: 336px;
    height: 100%;
    display: flex;
    flex-direction: column;
  `,
  ParamContainer: styled.div`
    & > input {
      width: 100%;
    }
  `,
  ParamTitle: styled(Title)<{ required?: boolean }>`
    margin-bottom: 8px;
    ${({ required }) =>
      required
        ? `
        &:after {
          content: '*';
        }
        `
        : ''}
  `,
  LinkSection: styled.div`
    margin: 48px 0;
    flex: 1;
  `,
  LinkHeader: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  `,
  LinkTitle: styled(Title)`
    font-weight: bold;
  `,
  Link: styled.div<{ disabled: boolean }>`
    background: ${({ theme }) => theme.secondary.chalk};
    border-radius: 8px;
    padding: 13px 15px;
    font-size: 14px;
    line-height: 18px;
    color: ${({ disabled, theme }) =>
      disabled ? addAlpha(theme.secondary.softBlue2, 0.4) : theme.secondary.softBlue2};
    overflow-wrap: break-word;
  `,
  Footer: styled.div`
    font-weight: bold;
    font-size: 12px;
    line-height: 15px;
    color: ${({ theme }) => theme.algoanBlue};
  `,
  Anchor: styled.a`
    color: unset;

    &:hover {
      font-weight: 700;
    }
  `,
  Input: styled(Input)`
    margin-top: 24px;
  `,
};
